import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/context/AuthContext";
import { useCrumbs } from "@/context/CrumbContext";
import { CityRiskStats, HeatBlobs, HeatDefs, RiskRanking } from "@/components/RiskHeat";
import { ArrowLeft, Building2, Flame, MapPin, X } from "lucide-react";

// Rough city coordinates (as % within 1000x480 viewBox).
const CITIES = {
  "istanbul": { name: "İstanbul", x: 325, y: 95, auth: "ibb" },
  "ankara": { name: "Ankara", x: 530, y: 180, auth: "abb" },
  "izmir": { name: "İzmir", x: 220, y: 230, auth: "izmir" },
  "bursa": { name: "Bursa", x: 325, y: 140, auth: "bursa" },
  "antalya": { name: "Antalya", x: 405, y: 325, auth: "antalya" },
  "kocaeli": { name: "Kocaeli", x: 360, y: 110, auth: null },
  "konya": { name: "Konya", x: 490, y: 265, auth: null },
  "adana": { name: "Adana", x: 630, y: 310, auth: null },
  "gaziantep": { name: "Gaziantep", x: 720, y: 300, auth: null },
  "trabzon": { name: "Trabzon", x: 760, y: 125, auth: null },
  "samsun": { name: "Samsun", x: 630, y: 110, auth: null },
  "erzurum": { name: "Erzurum", x: 830, y: 180, auth: null },
  "diyarbakir": { name: "Diyarbakır", x: 790, y: 275, auth: null },
  "van": { name: "Van", x: 910, y: 240, auth: null },
  "kayseri": { name: "Kayseri", x: 585, y: 230, auth: null },
  "manisa": { name: "Manisa", x: 250, y: 215, auth: null },
  "turgutlu": { name: "Turgutlu", x: 265, y: 230, auth: "izmir" },
  "mersin": { name: "Mersin", x: 575, y: 325, auth: null },
};

// Simplified SVG path approximating Turkey's outline.
const TURKEY_PATH = "M90,195 L135,115 L190,100 L245,80 L300,80 L360,95 L420,95 L470,110 L510,115 L560,105 L625,100 L690,110 L745,120 L810,150 L870,170 L915,195 L915,235 L900,275 L865,310 L810,340 L750,355 L690,355 L620,345 L560,325 L490,325 L420,335 L350,330 L285,315 L220,290 L160,255 L120,230 Z";

export default function TurkeyMap() {
  const navigate = useNavigate();
  useCrumbs([{ label: "Türkiye Haritası", to: "/map" }]);
  const [projects, setProjects] = useState([]);
  const [authorities, setAuthorities] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [classes, setClasses] = useState([]);
  const [risk, setRisk] = useState({ cities: [], formula: "" });
  const [heatOn, setHeatOn] = useState(true);

  useEffect(() => { (async () => {
    const [p, a, c, r] = await Promise.allSettled([
      apiClient.get("/projects"), apiClient.get("/catalog/local-authorities"),
      apiClient.get("/catalog/building-classes"), apiClient.get("/analytics/city-risk"),
    ]);
    if (p.status === "fulfilled") setProjects(p.value.data || []);
    if (a.status === "fulfilled") setAuthorities(a.value.data || []);
    if (c.status === "fulfilled") setClasses(c.value.data || []);
    if (r.status === "fulfilled") setRisk(r.value.data);
  })(); }, []);

  // Group projects by city (parsed from location string)
  const projectsByCity = useMemo(() => {
    const g = {};
    projects.forEach((p) => {
      const loc = (p.location || "").toLowerCase();
      Object.keys(CITIES).forEach((key) => {
        if (loc.includes(key) || loc.includes(CITIES[key].name.toLowerCase())) {
          g[key] = g[key] || [];
          g[key].push(p);
        }
      });
    });
    return g;
  }, [projects]);

  const authorityByKey = (key) => authorities.find((a) => a.id === CITIES[key]?.auth);
  const selected = selectedKey ? CITIES[selectedKey] : null;
  const selectedAuth = selected ? authorityByKey(selectedKey) : null;
  const selectedProjects = selectedKey ? (projectsByCity[selectedKey] || []) : [];

  return (
    <div className="detail-shell" data-testid="turkey-map">
      <header className="detail-topbar">
        <button className="icon-button" onClick={() => navigate("/home")} data-testid="map-back"><ArrowLeft size={17}/></button>
        <div className="detail-head">
          <div className="eyebrow">PROJE HARİTASI · TÜRKİYE</div>
          <h1>Yerleşim özeti</h1>
          <small><Building2 size={13}/> {projects.length} proje · {Object.keys(projectsByCity).length} şehir</small>
        </div>
      </header>
      <div className="detail-body">
        <section className="section-block">
          <div className="map-toolbar">
            <button className={`chip ${heatOn ? "active" : ""}`} onClick={() => setHeatOn((v) => !v)} data-testid="heat-toggle">
              <Flame size={12}/> Isı katmanı {heatOn ? "açık" : "kapalı"}
            </button>
            <small>{risk.cities.length} şehirde açık bulgu · {risk.cities.reduce((s, r) => s + r.open, 0)} toplam</small>
          </div>
          <div className="map-grid">
          <div className="map-stage" data-testid="map-stage">
            <svg viewBox="0 0 1000 480" className="turkey-svg">
              <defs>
                <linearGradient id="landGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#1e293b"/>
                  <stop offset="1" stopColor="#0f172a"/>
                </linearGradient>
                <HeatDefs/>
              </defs>
              <path d={TURKEY_PATH} fill="url(#landGrad)" stroke="#334155" strokeWidth="1.5"/>
              {heatOn && <HeatBlobs cities={CITIES} risk={risk.cities}/>}
              {/* Decorative label */}
              <text x="500" y="40" textAnchor="middle" fill="#64748b" fontSize="12" fontFamily="DM Mono">TÜRKİYE · PROJE HARİTASI</text>
              {Object.entries(CITIES).map(([key, c]) => {
                const auth = authorityByKey(key);
                const projCount = (projectsByCity[key] || []).length;
                const color = auth ? auth.color : (projCount ? "#f59e0b" : "#475569");
                const r = projCount ? 8 + Math.min(projCount, 5) : 5;
                return (
                  <g key={key} className="city-group" onClick={() => setSelectedKey(key)} data-testid={`city-${key}`} style={{cursor:"pointer"}}>
                    <circle cx={c.x} cy={c.y} r={r + 5} fill={color} opacity="0.2"/>
                    <circle cx={c.x} cy={c.y} r={r} fill={color} stroke="#fff" strokeWidth="1.5"/>
                    <text x={c.x} y={c.y - r - 5} textAnchor="middle" fontSize="10" fill="#dbe3ef" fontFamily="DM Mono">
                      {c.name}{projCount ? ` · ${projCount}` : ""}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
          <RiskRanking risk={risk.cities} formula={risk.formula} onSelect={setSelectedKey}/>
          </div>
          <div className="map-legend">
            {authorities.filter((a) => a.id !== "none").map((a) => (
              <span key={a.id} className="chip" style={{borderColor: a.color, color: a.color}}>● {a.short}</span>
            ))}
            <span className="chip" style={{borderColor: "#f59e0b", color: "#f59e0b"}}>● Projeleriniz</span>
            <span className="chip">● Diğer şehir</span>
          </div>
        </section>
      </div>

      {selected && (
        <div className="modal-backdrop" onClick={() => setSelectedKey(null)} data-testid="city-modal">
          <div className="modal wide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div>
                <div className="eyebrow">{selectedAuth?.region || "ŞEHİR"}</div>
                <h2><MapPin size={18}/> {selected.name}</h2>
              </div>
              <button className="icon-button" onClick={() => setSelectedKey(null)} data-testid="city-modal-close"><X size={17}/></button>
            </div>
            <div className="step-body">
              <div className="eyebrow">AÇIK BULGU RİSKİ</div>
              <CityRiskStats row={risk.cities.find((r) => r.city === selectedKey)}/>
              {selectedAuth ? (
                <>
                  <div className="class-detail" style={{borderColor: selectedAuth.color}}>
                    <strong style={{color: selectedAuth.color}}>{selectedAuth.name}</strong>
                    <small>{selectedAuth.description}</small>
                    <small>{selectedAuth.notes}</small>
                  </div>
                  <div className="eyebrow" style={{marginTop:4}}>EK ŞARTLAR</div>
                  <div className="blocker-list">
                    {selectedAuth.overrides.map((o, i) => (
                      <div key={i} className="blocker-item ok" data-testid={`override-${o.check_id}`}>
                        <span>{i+1}</span>
                        <p><strong>{o.title}</strong><br/><small>{o.source}</small></p>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <small className="evidence-empty">Bu şehir için özel yerel şart tanımlanmamış · yalnızca ulusal BYKHY uygulanır.</small>
              )}
              {selectedProjects.length > 0 && (
                <>
                  <div className="eyebrow" style={{marginTop:6}}>BU ŞEHİRDEKİ PROJELERİNİZ</div>
                  <div className="assessment-list">
                    {selectedProjects.map((p) => (
                      <button key={p.id} className="assessment-row" onClick={() => navigate(`/assessments?q=${encodeURIComponent(p.name)}`)} data-testid={`city-project-${p.id}`}>
                        <div className="assessment-main"><strong>{p.name}</strong><small>{p.location}</small></div>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
